import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import AdminShell from "./AdminShell";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminContent() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [opening, setOpening] = useState("");
  const [paragraphsText, setParagraphsText] = useState("");
  const [signature, setSignature] = useState("");
  const [intro, setIntro] = useState("");
  const [totalLine, setTotalLine] = useState("");
  const [timelinesNote, setTimelinesNote] = useState("");
  // Design process
  const [dpEyebrow, setDpEyebrow] = useState("");
  const [dpTitle, setDpTitle] = useState("");
  const [dpIntro, setDpIntro] = useState("");
  // Working together
  const [wtEyebrow, setWtEyebrow] = useState("");
  const [wtTitle, setWtTitle] = useState("");
  const [wtIntro, setWtIntro] = useState("");
  const [wtOutroTitle, setWtOutroTitle] = useState("");
  const [wtOutroBody, setWtOutroBody] = useState("");
  const [wtOutroClosing, setWtOutroClosing] = useState("");
  // Terms
  const [termsIntro, setTermsIntro] = useState("");
  const [termsClosing, setTermsClosing] = useState("");
  // Rate card
  const [rateCardUrl, setRateCardUrl] = useState("");
  const [rateCardUpdatedAt, setRateCardUpdatedAt] = useState(null);
  const [rateCardBusy, setRateCardBusy] = useState(false);
  // Moodboard editorial site list (used by the auto-generator)
  const [editorialSitesText, setEditorialSitesText] = useState("");

  const hydrate = (c) => {
    setOpening(c?.founders_letter?.opening || "");
    setParagraphsText((c?.founders_letter?.paragraphs || []).join("\n\n"));
    setSignature(c?.founders_letter?.signature || "");
    setIntro(c?.roadmap?.intro || "");
    setTotalLine(c?.roadmap?.total_line || "");
    setTimelinesNote(c?.roadmap?.timelines_note || "");
    setDpEyebrow(c?.design_process?.eyebrow || "");
    setDpTitle(c?.design_process?.title || "");
    setDpIntro((c?.design_process?.intro_paragraphs || []).join("\n\n"));
    setWtEyebrow(c?.working_together?.eyebrow || "");
    setWtTitle(c?.working_together?.title || "");
    setWtIntro(c?.working_together?.intro || "");
    setWtOutroTitle(c?.working_together?.outro_title || "");
    setWtOutroBody(c?.working_together?.outro_body || "");
    setWtOutroClosing(c?.working_together?.outro_closing || "");
    setTermsIntro(c?.terms?.intro || "");
    setTermsClosing(c?.terms?.closing || "");
    setRateCardUrl(c?.rate_card?.pdf_url || "");
    setRateCardUpdatedAt(c?.rate_card?.updated_at || null);
    setEditorialSitesText((c?.moodboard_editorial_sites || []).join("\n"));
  };

  const uploadRateCard = async (file) => {
    if (!file) return;
    if (!(file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf"))) {
      toast.error("Please choose a PDF file.");
      return;
    }
    setRateCardBusy(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await axios.post(`${API}/admin/rate-card/upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setRateCardUrl(data.pdf_url);
      setRateCardUpdatedAt(data.updated_at);
      toast.success("Rate card updated — homeowners will see the new PDF instantly.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed — try again.");
    } finally { setRateCardBusy(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/content`);
        hydrate(data);
      } catch (e) {
        toast.error("Couldn't load content");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        founders_letter: {
          opening: opening.trim(),
          paragraphs: paragraphsText.split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean),
          signature: signature.trim(),
        },
        roadmap: {
          intro: intro.trim(),
          total_line: totalLine.trim(),
          timelines_note: timelinesNote.trim(),
        },
        design_process: {
          eyebrow: dpEyebrow.trim(),
          title: dpTitle.trim(),
          intro_paragraphs: dpIntro.split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean),
        },
        working_together: {
          eyebrow: wtEyebrow.trim(),
          title: wtTitle.trim(),
          intro: wtIntro.trim(),
          outro_title: wtOutroTitle.trim(),
          outro_body: wtOutroBody.trim(),
          outro_closing: wtOutroClosing.trim(),
        },
        terms: {
          intro: termsIntro.trim(),
          closing: termsClosing.trim(),
        },
        moodboard_editorial_sites: editorialSitesText
          .split(/\r?\n/)
          .map((s) => s.trim().replace(/^https?:\/\//, "").replace(/\/+$/, "").toLowerCase())
          .filter(Boolean),
      };
      const { data } = await axios.put(`${API}/content`, payload);
      hydrate(data);
      toast.success("Content saved");
    } catch (e) {
      toast.error("Couldn't save, please try again");
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = async () => {
    if (!window.confirm("Reset all content back to the built-in defaults?")) return;
    try {
      const { data } = await axios.post(`${API}/content/reset`);
      hydrate(data);
      toast.success("Reset to defaults");
    } catch {
      toast.error("Couldn't reset");
    }
  };

  return (
    <AdminShell
      eyebrow="Admin"
      title="Content"
    >
      <div style={{ color: "var(--folk-muted)", fontSize: 14, marginBottom: 20 }}>
        Edit the founders letter, roadmap, design process, working together and terms copy that appears in every homeowner portal.
      </div>
      {loading ? (
        <div style={{ color: "var(--folk-muted)" }}>Loading…</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
            {/* Founders letter */}
            <Card title="Founders letter" hint="Shown on the Welcome tab of every portal. The homeowner's name is added automatically at the top.">
              <Field label="Opening line">
                <input data-testid="opening" className="folk-input" value={opening} onChange={(e) => setOpening(e.target.value)} />
              </Field>
              <Field label="Body paragraphs" hint="Separate each paragraph with a blank line.">
                <textarea data-testid="paragraphs" rows={16} className="folk-textarea" value={paragraphsText} onChange={(e) => setParagraphsText(e.target.value)} />
              </Field>
              <Field label="Signature" hint="Use line breaks, e.g. 'Warmly,' / 'Nupur & Karn' / 'Founders, Folk'.">
                <textarea data-testid="signature" rows={4} className="folk-textarea" value={signature} onChange={(e) => setSignature(e.target.value)} />
              </Field>
            </Card>

            {/* Roadmap */}
            <Card title="Roadmap" hint="Copy that surrounds the numbered phase table on the Roadmap tab.">
              <Field label="Intro paragraph">
                <textarea data-testid="intro" rows={3} className="folk-textarea" value={intro} onChange={(e) => setIntro(e.target.value)} />
              </Field>
              <Field label="Total-time headline">
                <input data-testid="total-line" className="folk-input" value={totalLine} onChange={(e) => setTotalLine(e.target.value)} />
              </Field>
              <Field label="'A note on timelines' body">
                <textarea data-testid="timelines-note" rows={5} className="folk-textarea" value={timelinesNote} onChange={(e) => setTimelinesNote(e.target.value)} />
              </Field>
            </Card>

            {/* Design process */}
            <Card title="Design process" hint="Shown on the Design process tab. Numbered phases stay code-defined; edit the headline + intro copy here.">
              <Field label="Eyebrow">
                <input data-testid="dp-eyebrow" className="folk-input" value={dpEyebrow} onChange={(e) => setDpEyebrow(e.target.value)} />
              </Field>
              <Field label="Title">
                <input data-testid="dp-title" className="folk-input" value={dpTitle} onChange={(e) => setDpTitle(e.target.value)} />
              </Field>
              <Field label="Intro paragraphs" hint="Separate each paragraph with a blank line.">
                <textarea data-testid="dp-intro" rows={5} className="folk-textarea" value={dpIntro} onChange={(e) => setDpIntro(e.target.value)} />
              </Field>
            </Card>

            {/* Working together */}
            <Card title="Working together" hint="Shown on the Working together tab. Numbered lists stay code-defined; edit the top + closing copy here.">
              <Field label="Eyebrow">
                <input data-testid="wt-eyebrow" className="folk-input" value={wtEyebrow} onChange={(e) => setWtEyebrow(e.target.value)} />
              </Field>
              <Field label="Title">
                <input data-testid="wt-title" className="folk-input" value={wtTitle} onChange={(e) => setWtTitle(e.target.value)} />
              </Field>
              <Field label="Intro paragraph">
                <textarea data-testid="wt-intro" rows={4} className="folk-textarea" value={wtIntro} onChange={(e) => setWtIntro(e.target.value)} />
              </Field>
              <Field label="'One last thing' — title">
                <input data-testid="wt-outro-title" className="folk-input" value={wtOutroTitle} onChange={(e) => setWtOutroTitle(e.target.value)} />
              </Field>
              <Field label="'One last thing' — body">
                <textarea data-testid="wt-outro-body" rows={5} className="folk-textarea" value={wtOutroBody} onChange={(e) => setWtOutroBody(e.target.value)} />
              </Field>
              <Field label="'One last thing' — closing line">
                <input data-testid="wt-outro-closing" className="folk-input" value={wtOutroClosing} onChange={(e) => setWtOutroClosing(e.target.value)} />
              </Field>
            </Card>

            {/* Terms */}
            <Card title="Terms" hint="Shown on the Terms tab. Legal sections stay code-defined; edit the summary intro + closing note here.">
              <Field label="Intro paragraph">
                <textarea data-testid="terms-intro" rows={4} className="folk-textarea" value={termsIntro} onChange={(e) => setTermsIntro(e.target.value)} />
              </Field>
              <Field label="Closing note">
                <textarea data-testid="terms-closing" rows={4} className="folk-textarea" value={termsClosing} onChange={(e) => setTermsClosing(e.target.value)} />
              </Field>
            </Card>

            {/* Rate card */}
            <Card title="Rate card PDF" hint="This is the PDF homeowners download from their report page. Upload a new PDF to replace it for everyone, instantly.">
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {rateCardUrl && (
                  <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12, background: "var(--folk-bg)", border: "1px solid var(--folk-border)", borderRadius: 10, padding: "10px 14px" }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--folk-muted)", fontWeight: 700 }}>Current file</div>
                      <a
                        href={rateCardUrl}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="rate-card-current-link"
                        style={{ color: "var(--folk-accent)", fontFamily: "Host Grotesk, sans-serif", fontSize: 13.5, textDecoration: "underline", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      >
                        {rateCardUrl}
                      </a>
                      {rateCardUpdatedAt && (
                        <div style={{ fontSize: 11.5, color: "var(--folk-hint)", fontFamily: "Host Grotesk, sans-serif" }}>
                          Updated {new Date(rateCardUpdatedAt).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <label
                  data-testid="rate-card-upload-label"
                  style={{
                    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                    gap: 6, background: "#FFFBC9", border: "1.5px dashed rgba(104, 40, 14, 0.4)",
                    borderRadius: 12, padding: "22px 16px", cursor: rateCardBusy ? "wait" : "pointer",
                    textAlign: "center",
                  }}
                >
                  <input
                    type="file"
                    accept="application/pdf"
                    data-testid="rate-card-upload-input"
                    style={{ display: "none" }}
                    disabled={rateCardBusy}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) uploadRateCard(f);
                      e.target.value = "";
                    }}
                  />
                  <div style={{ fontFamily: "Playfair Display, serif", letterSpacing: "-0.028em", fontSize: 18, color: "#68280E", textTransform: "lowercase" }}>
                    {rateCardBusy ? "uploading…" : "drop a new pdf here"}
                  </div>
                  <div style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 13, color: "var(--folk-muted)" }}>
                    or click to select. PDF only, up to 25 MB.
                  </div>
                </label>
              </div>
            </Card>

            <Card
              title="Moodboard editorial sites"
              hint="Sites the auto-generator will search (Bing/DuckDuckGo with a site: filter) when building a homeowner's moodboard. One domain per line, no https://."
            >
              <Field
                label="Sites"
                hint="Order matters — earlier sites get more query slots. Blank lines are ignored."
              >
                <textarea
                  data-testid="editorial-sites-textarea"
                  value={editorialSitesText}
                  onChange={(e) => setEditorialSitesText(e.target.value)}
                  rows={12}
                  spellCheck={false}
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1.5px solid var(--folk-border)", fontFamily: "'Host Grotesk', ui-monospace, monospace", fontSize: 13.5, lineHeight: 1.55, background: "#FAF5F3", color: "#1B1A1A" }}
                />
              </Field>
            </Card>

            <div style={{ display: "flex", gap: 12, alignItems: "center", justifyContent: "flex-end" }}>
              <button
                type="button"
                onClick={resetToDefaults}
                data-testid="reset-btn"
                style={{ border: "1.5px solid var(--folk-border)", background: "transparent", color: "var(--folk-muted)", padding: "10px 18px", borderRadius: 999, cursor: "pointer", fontFamily: "Host Grotesk, sans-serif", fontSize: 14 }}
              >
                Reset to defaults
              </button>
              <button
                type="button"
                onClick={save}
                disabled={saving}
                data-testid="save-btn"
                className="folk-pill"
                style={{ opacity: saving ? 0.6 : 1 }}
              >
                {saving ? "Saving…" : "Save changes"}
              </button>
            </div>
          </div>
        )}
    </AdminShell>
  );
}

function Card({ title, hint, children }) {
  return (
    <div style={{ background: "var(--folk-card)", border: "1.5px solid var(--folk-border)", borderRadius: 18, padding: 24 }}>
      <div style={{ fontFamily: "Playfair Display, serif", letterSpacing: '-0.015em', fontSize: 22, marginBottom: 4 }}>{title}</div>
      {hint && <div style={{ color: "var(--folk-muted)", fontSize: 13.5, marginBottom: 18 }}>{hint}</div>}
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>{children}</div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <div style={{ fontSize: 13, color: "var(--folk-hint)", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</div>
      {hint && <div style={{ fontSize: 12.5, color: "var(--folk-muted)", marginBottom: 8 }}>{hint}</div>}
      {children}
    </div>
  );
}
